import {notNil, isEmpty, isNil, notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {log, fatal, warn} from '@megaphonenpm/mputils/src/logfunctions.js';
import {
  addStyleSheet,
  div,
  getFontFamilies, getStyleSheet,
  idToAttribute, removeCSSPropertiesFromRules,
} from '@megaphonenpm/mputils/src/DOM.js';
import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
import {CHANGE, COMPLETE, FAIL, INITED} from '@megaphonenpm/mputils/src/constants.js';
import {LoadError} from "@megaphonenpm/mputils/src/LoadError.js";
import {get} from 'lodash-es';
import {LoadTextTask} from '@megaphonenpm/mputils/src/LoadTextTask.js';
import {AwaitFontsTask} from '@megaphonenpm/mputils/src/AwaitFontsTask.js';
import {MegaScreen} from './MegaScreen.js';

// TODO: Make this class resilient to multiple redundant load attempts of the same theme.
//       Or possibly replace the existing theme with the newly requested version.
export class ThemeLoader extends EventDispatcher {
  constructor () {
    super();
    this.loadedThemes = new Set();
    this.queuedThemes = new Map();
  }

  loadThemes (themes) {
    if (isEmpty(themes)) {
      warn('No themes specified by server. No themes will be loaded.');
    } else {
      log(`Loading themes: [${themes.map(theme => get(theme, 'field_name')).join(", ")}]`);
    }
    let loadInProgress = this.queuedThemes.size > 0;
    this.queueThemes(themes);

    let loadNext = () => {
      if (this.queuedThemes.size > 0) {
        let nextEntry = this.queuedThemes.entries().next().value;
        this.queuedThemes.delete(nextEntry[0]);
        this.load(nextEntry[1]);
      }
    };

    let initedListener = e => {
      // Update theme loaded counts
      this.loadedThemes.add(e.detail.id);

      // Inform observers that a theme has finished initializing
      this.dispatchEvent(CHANGE);

      // Dispatch complete event
      log('Theme [' + e.detail.name + '] initialization complete');
      log('Themes initialized: ' + this.numThemesLoaded + ' of ' + this.numThemes);
      if (this.queuedThemes.size === 0) {
        this.off(INITED, initedListener);
        this.dispatchEvent(COMPLETE);
      } else {
        loadNext();
      }
    };
    if (!loadInProgress) {
      this.on(INITED, initedListener);
      loadNext();
    }
  }

  queueThemes (themes) {
    if (notNil(themes)) {
      themes.forEach((theme) => {
        this.queuedThemes.set(get(theme, 'id'), theme);
      });
    }
  }

  load (theme) {
    // Initialize variables
    let themeID = get(theme, 'id');
    let themeFieldName = get(theme, 'field_name');
    let themeBaseURI = this.getThemeBaseURI(themeID);
    let themeURI = `${themeBaseURI}index.html`;

    // Prevent themes from loading more than once
    if (this.loadedThemes.has(themeID)) {
      log(`Request to load theme [${themeFieldName}] rejected. Theme already loaded.`);
      return;
    } else if (this.queuedThemes.has(themeID)) {
      log(`Request to load theme [${themeFieldName} (${themeID})] rejected. Theme already queued for loading.`);
      return;
    }

    // Create the top-level <div> to contain the loaded theme's HTML, and add the <div> to the DOm
    let themeDiv = div(idToAttribute(themeID));
    themeDiv.setAttribute('data-theme-name', themeFieldName);
    document.querySelector('#themes').append(themeDiv);

    log(`Loading theme [${themeFieldName}] from [${themeURI}]`);

    let loadTextTask = new LoadTextTask(themeURI, {nocache: true});
    loadTextTask.on(COMPLETE, () => {
      let themeRawData;
      // Theme data (raw HTML) has loaded. Make some adjustments now...
      // Add the theme's absolute (base) URL to all img src URLs
      themeRawData = loadTextTask.text.replace(/src="/g, 'src="' + themeBaseURI);
      // Add the theme's absolute (base) URL to all CSS URLs (e.g., fonts)
      themeRawData = themeRawData.replace(/url\("/g, 'url("' + themeBaseURI);
      // Insert the loaded theme's HTML into the theme's <div>
      themeDiv.innerHTML = themeRawData;
      // Initialize the theme
      this.initTheme(themeID).then(() => {
        // Inform listeners that the theme is ready for use.
        this.dispatchEvent(INITED, {id: themeID, name: themeFieldName});
      }).catch(reason => {
        fatal(`Failed to initialize theme [${themeFieldName}]. Error follows.`);
        fatal(reason);
        // The theme could not be initialized, so treat this attempt to load the theme as complete.
        this.dispatchEvent(INITED, {id: themeID, name: themeFieldName});
      });
    });

    loadTextTask.on(FAIL, (e) => {
      // Theme data (raw HTML) failed to load. Handle load failures.
      let error = e.detail.error;
      if (error instanceof LoadError) {
        fatal(`Failed to load theme [${themeFieldName}]. HTTP status ${error.HTTPStatus}. URI: ${error.uri}`);
      } else {
        fatal(`Failed to load theme [${themeFieldName}]. Either the Internet connection is 
          unavailable or an error occurred during theme initialization. Error follows.`);
        fatal(error);
      }
      // The theme could not be loaded or initialized, so treat this attempt to load the
      // theme as complete. The theme will be considered initialized, but not valid. (See
      // isValidGWDTheme() in GWDService).
      this.dispatchEvent(INITED, {id: themeID, name: themeFieldName});
    });

    loadTextTask.start();
  }

  /**
   * Processes a loaded theme for use in MegaScreen
   */
  initTheme (id) {
    return new Promise((resolve, reject) => {
      // Initialize the loaded theme for use by MegaScreen.

      // SAFARI WORKAROUND (Part 1):
      // In Safari (only), when <link> elements for Google Fonts are present in the loaded theme, the
      // <style> element that is copied to the document <head> by postProcessThemeCSS() becomes
      // inaccessible via document.styleSheets for at least 100ms (sometimes as long as 1000ms).
      // By contrast, when no <link> elements are present in the loaded theme, the <style> element
      // is accessible immediately. To allow the theme's copied <style> element to be accessed
      // immediately, remove all <link> elements from the loaded theme (and replace them after
      // CSS post-processing is complete). No other browser seems to exhibit Safari's behavior.
      let linkElements = this.getThemeDiv(id).querySelectorAll('link');
      linkElements.forEach(element => element.remove());

      // Adjust theme's CSS and move it to the MegaScreen DOM
      let styleElement = this.postProcessThemeCSS(id);

      // Remove GWD styles that are used at author-time but are unwanted in MegaScreen
      this.stripAuthorTimeCSS(id).then(() => {
        // SAFARI WORKAROUND (Part 2):
        // Replace <link> elements (see "SAFARI WORKAROUND (Part 1)", above)
        linkElements.forEach(element => document.head.append(element));

        // Wait for the theme's fonts to load
        this.waitForFonts(styleElement, MegaScreen.globals.themeService.getThemeFieldName(id)).then(() => {
          // Once fonts have loaded, this theme has finished initializing, so
          // resolve the initTheme() Promise.
          resolve();
        });
      }).catch(reason => {
        // Attempt to strip styles failed. Reject the initTheme() Promise so the problem can be
        // handled upstream.
        reject(reason);
      });
    });
  }

  waitForFonts (styleElement, themeName) {
    return new Promise((resolve) => {
        // Wait for fonts to load
        let awaitFontsTask = new AwaitFontsTask(styleElement.sheet);
        awaitFontsTask.on(COMPLETE, () => {
          log('All fonts loaded for theme [' + themeName + ']');
          resolve();
        });
        awaitFontsTask.start();
      }
    );
  }

  get numThemes () {
    return this.queuedThemes.size + this.loadedThemes.size;
  }

  get numThemesLoaded () {
    return this.loadedThemes.size;
  }

  getThemeBaseURI (themeID) {
    let themeBaseURI = ThemeLoader.MAIN_THEME_URI;
    themeBaseURI = themeBaseURI.replace('$env', MegaScreen.globals.dataStore.environment);
    if (notEmpty(themeID)) {
      themeBaseURI += `${themeID}/`;
    }
    return themeBaseURI;
  }

  getThemeDiv (themeID) {
    return document.querySelector(`#themes #_${themeID}`);
  }

  /**
   * Prepares the CSS of loaded themes for use by MegaScreen, as follows:
   *
   * -Concatenates all styles into one sheet, which is placed in the document's <head>. (A best practice.)
   * -Adds a theme-specific CSS-rule prefix to prevent style conflicts across multiple themes. This cannot
   *  be done directly to the loaded stylesheet because selectorText is read-only in some browsers.
   * -Adds a theme-specific suffix to keyframes-names for animations created by GWD. The suffix prevents
   *  conflicts between keyframes-names across multiple Google Web Designer theme files. (CSS @keyframe rules
   *  are global, and cannot be namespaced, so the namespace-suffix must be applied to the keyframes-name).
   * -Removes the theme's <style> elements from the theme's <div> (to avoid unwanted duplication and bloat)
   */
  postProcessThemeCSS (themeID) {
    let themeName = MegaScreen.globals.themeService.getThemeFieldName(themeID);
    let styleElements = Array.from(MegaScreen.globals.themeLoader.getThemeDiv(themeID).querySelectorAll('style'));
    let stylesheets = styleElements.map(element => element.sheet);
    let rulePrefix = MegaScreen.globals.themeService.getCSSPrefixForTheme(themeID)+' ';  // Use + here to call attention to the very important empty space

    let cssStrings = [];
    let clonedRule = '';
    let isBodyOrHTMLRule = false;
    for (let i = 0, numSheets = stylesheets.length; i < numSheets; i++) {
      let cssRules = stylesheets[i].cssRules;
      for (let j = 0, numRules = cssRules.length; j < numRules; j++) {
        clonedRule = cssRules[j].cssText;

        // To prevent naming conflicts across themes, add a theme-id suffix to any keyframes-names
        // generated by Google Web Designer.
        // TODO: Consider adding the suffix using CSSKeyframesRule.name rather than this brute-force regex
        //  string replacement. See: https://developer.mozilla.org/en-US/docs/Web/API/CSSKeyframesRule
        clonedRule = clonedRule.replace(/gwd-keyframes/g, 'gwd-keyframes_'+themeName);

        // If the rule is not an "at rule" (e.g., @font-face), and does not start with "html" or "body",
        // then add the theme's rule prefix
        isBodyOrHTMLRule = clonedRule.indexOf('html') === 0 || clonedRule.indexOf('body') === 0;
        if (clonedRule.indexOf('@') !== 0
          && !isBodyOrHTMLRule) {
          // Special case for the .exporter rule. In GWD themes, global font styles can be set in the
          // .exporter rule, but .exporter does not exist in the MegaScreen #runviews <div>. Hence,
          // copy the desired properties from the .exporter rule to the theme's instantiated <div>'s rule.
          if (clonedRule.indexOf('.exporter') === 0) {
            let fontFamily = get(cssRules[j], 'style.fontFamily');
            let fontFamilyProperty = isEmpty(fontFamily) ? '' : `font-family: ${fontFamily};`;
            let fontWeight = get(cssRules[j], 'style.fontWeight');
            let fontWeightProperty = isEmpty(fontWeight) ? '' : `font-weight: ${fontWeight};`;
            let fontSize = get(cssRules[j], 'style.fontSize');
            let fontSizeProperty = isEmpty(fontSize) ? '' : `font-size: ${fontSize};`;
            let color = get(cssRules[j], 'style.color');
            let colorProperty = isEmpty(color) ? '' : `color: ${color};`;
            let lineHeight = get(cssRules[j], 'style.lineHeight');
            let lineHeightProperty = isEmpty(lineHeight) ? '' : `line-height: ${lineHeight};`;
            clonedRule = `{${fontFamilyProperty}${fontSizeProperty}${fontWeightProperty}${colorProperty}${lineHeightProperty}}`;
          }
          clonedRule = rulePrefix+clonedRule;
        }

        if (!isBodyOrHTMLRule) {
          cssStrings.push(clonedRule);
        }
      }
      // Done processing this sheet, so remove its original <style> element from the loaded theme <div>
      stylesheets[i].ownerNode.remove();
    }
    // Add the concatenated styles to the document <head>
    let style = addStyleSheet(themeName);
    style.innerHTML = cssStrings.join('\n');
    return style;
  }

  /**
   * Removes certain CSS properties that are needed in GWD for theme-production workflows,
   * but conflict with MegaScreen's automated layouts.
   *
   * @param themeID
   */
  stripAuthorTimeCSS (themeID) {
    let themeName = MegaScreen.globals.themeService.getThemeFieldName(themeID);
    let themeCSSPrefix = MegaScreen.globals.themeService.getCSSPrefixForTheme(themeID);
    return new Promise((resolve, reject) => {
      // Prepare first set of rules and properties to cleanse
      let ruleBlacklist1 = ['.answerTextContainer', '.percentContainer', '.surveyBarContainer',
        '.average_vote_txt', '.minMaxLabel', '.guessgaugeValueLabel',
        '.moodmeterValueLabel', '.moodmeterDial', '.secondary_title_txt'];
      ruleBlacklist1 = ruleBlacklist1.map(value => `${themeCSSPrefix} ${value}`);
      let propertyBlacklist1 = ['position', 'width', 'height', 'left', 'top'];

      // Prepare second set of rules and properties to cleanse. This set does not strip width/height,
      // so that author-time dimensions are preserved for use in dynamic layouts in MegaScreen.
      let ruleBlacklist2 = ['.fivestarsResult', '.guessgaugeResult', '.moodmeterResult', '.averageVoteResult',
        '.guessgauge_value_label_txt', '.moodmeterDialBG','.moodmeter_value_label_txt', '.StarForeground', '.StarBackground'];
      ruleBlacklist2 = ruleBlacklist2.map(value => `${themeCSSPrefix} ${value}`);
      let propertyBlacklist2 = ['position', 'left', 'top'];

      // Cleanse unwanted properties from CSS rules
      let numCSSPropertyRemovalAttempts = 0;
      let maxCSSPropertyRemovalAttempts = 2;
      let doCSSPropertyRemoval = () => {
        numCSSPropertyRemovalAttempts++;
        let themeStyleSheet = getStyleSheet(themeName);
        if (isNil(themeStyleSheet)) {
          if (numCSSPropertyRemovalAttempts < maxCSSPropertyRemovalAttempts) {
            // In Safari, during development, in some very rare cases (1 in 50 loads), the stylesheet is not immediately
            // available via document.styleSheets immediately after it has been added to the document <head> via addStyleSheet().
            // However, after the DOM has updated, the stylesheet might become available, so poll for it. This issue has
            // never been observed in other browsers. The exact cause for the failure in Safari is currently unknown. The code
            // here is a workaround, not a fix, for the problem.
            warn(`Failed to retrieve theme stylesheet for theme ${themeName}. Another attempt will be made in 1 second.`
              + `Number of attempts remaining: ${maxCSSPropertyRemovalAttempts-numCSSPropertyRemovalAttempts}`);
            setTimeout(doCSSPropertyRemoval, 1000);
          } else {
            warn(`Failed to retrieve theme stylesheet for theme ${themeName}. No further attempts will be made.`
                 + ' Cannot strip GWD author-time styles. Theme content will render with visual flaws.');
            resolve();
          }
        } else {
          if (numCSSPropertyRemovalAttempts > 1) {
            warn(`Retrieved theme stylesheet for theme ${themeName} after ${numCSSPropertyRemovalAttempts} attempts.`);
          }
          try {
            removeCSSPropertiesFromRules(themeStyleSheet, ruleBlacklist1, propertyBlacklist1);
            removeCSSPropertiesFromRules(themeStyleSheet, ruleBlacklist2, propertyBlacklist2);
            resolve();
          } catch (e) {
            reject(e);
          }
        }
      };
      doCSSPropertyRemoval();
    });
  }
}

/**
 * The central location at which themes are stored. This URI is used in the conversion of URIs in
 * loaded themes from relative to absolute format.
 */
ThemeLoader.MAIN_THEME_URI = 'https://themes.megaphonetv.com/$env/screen/';
